import { useState } from "react";
import {
  getAdminComparatorFirms,
  deleteComparatorFirm,
  useQuery,
  useAction,
} from "wasp/client/operations";
import type { ComparatorFirm } from "wasp/entities";
import { Button } from "../../../client/components/ui/button";
import { Badge } from "../../../client/components/ui/badge";
import { useToast } from "../../../client/hooks/use-toast";
import { Plus, Pencil, Trash2 } from "lucide-react";
import FirmFormDialog from "./FirmFormDialog";

export default function FirmsTab() {
  const { data: firms, refetch } = useQuery(getAdminComparatorFirms);
  const deleteFirm = useAction(deleteComparatorFirm);
  const { toast } = useToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ComparatorFirm | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (firm: ComparatorFirm) => {
    setEditing(firm);
    setDialogOpen(true);
  };

  const handleDelete = async (firm: ComparatorFirm) => {
    if (!window.confirm(`Delete firm "${firm.name}" and all its offers?`)) return;
    try {
      await deleteFirm({ id: firm.id });
      toast({ title: "Firm deleted" });
      refetch();
    } catch (e) {
      toast({ title: (e as Error).message, variant: "destructive" });
    }
  };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          New firm
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Slug</th>
              <th className="px-4 py-3 font-medium">Rating</th>
              <th className="px-4 py-3 font-medium">Published</th>
              <th className="px-4 py-3 font-medium">Sort</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {firms?.map((firm) => (
              <tr key={firm.id} className="border-b border-border last:border-0">
                <td className="px-4 py-3 font-medium text-foreground">{firm.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{firm.slug}</td>
                <td className="px-4 py-3 font-mono">
                  {firm.rating != null ? firm.rating.toFixed(1) : "—"}
                </td>
                <td className="px-4 py-3">
                  {firm.isPublished ? (
                    <Badge variant="default">Yes</Badge>
                  ) : (
                    <Badge variant="secondary">No</Badge>
                  )}
                </td>
                <td className="px-4 py-3 font-mono">{firm.sortOrder}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(firm)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(firm)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {firms && firms.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  No firms yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <FirmFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        firm={editing}
        onSaved={() => refetch()}
      />
    </div>
  );
}
